import dynamic from 'next/dynamic';
import { ComponentType } from 'react';
import { preloadable } from './preloadable';
import { withAllowedProps } from './withAllowedProps';

// Every Plasmic module here is loaded ONLY through its entry's dynamic() import — including its
// `__ArgProps` list. These lists are runtime arrays, so importing them statically (as this file
// used to) pulled every component module, and its CSS modules, into the static graph of every
// page that renders a <Fragment/>: /s/ shipped booking/profile/ravi CSS it never uses.
// docs/performance.md §۳.۱۰

type FragmentEntry = { Component: ComponentType<any>; id: string; projectId: string; preload?: () => Promise<void> };

export const Components: Record<string, FragmentEntry> = {
  Claim: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24/PlasmicClaim').then(m => withAllowedProps(m.default, m.PlasmicClaim__ArgProps))),
    id: '5jjwlzFYfMqI',
    projectId: 'iDYgiKJB9Yi7CUB81stQBK',
  },
  ReviewList2: {
    Component: dynamic(() =>
      Promise.all([import('.plasmic/ReviewList2'), import('.plasmic/plasmic/ravi_r_r/PlasmicReviewList')]).then(([c, a]) => withAllowedProps(c.default, a.PlasmicReviewList__ArgProps)),
    ),
    id: 'Kz2u6VAJ02yE',
    projectId: 'qQzsBf58SqzNJX45iggq96',
  },
  RateAndCommentCount2: {
    Component: dynamic(() =>
      Promise.all([import('.plasmic/plasmic/ravi_r_r/PlasmicReviewRateAndCommentCount'), import('.plasmic/plasmic/ravi_r_r/PlasmicRateAndCommentCount')]).then(([c, a]) => withAllowedProps(c.default, a.PlasmicRateAndCommentCount__ArgProps)),
    ),
    id: 'etOCIhcu_Yx5',
    projectId: 'qQzsBf58SqzNJX45iggq96',
  },
  RateProgressList: {
    Component: dynamic(() => import('.plasmic/plasmic/ravi_r_r/PlasmicReviewProgressList').then(m => withAllowedProps(m.default, m.PlasmicReviewProgressList__ArgProps))),
    id: 'xpGRRNKB86D2',
    projectId: 'qQzsBf58SqzNJX45iggq96',
  },
  RateAndReviews: {
    Component: dynamic(() => import('.plasmic/plasmic/ravi_r_r/PlasmicReviewRateAndReviews').then(m => withAllowedProps(m.default, m.PlasmicReviewRateAndReviews__ArgProps))),
    id: 'qb59XTke1gWO',
    projectId: 'qQzsBf58SqzNJX45iggq96',
  },
  ReviewList: {
    Component: dynamic(() =>
      Promise.all([import('.plasmic/ReviewList'), import('.plasmic/plasmic/ravi_r_r/PlasmicReviewList')]).then(([c, a]) => withAllowedProps(c.default, a.PlasmicReviewList__ArgProps)),
    ),
    id: 'Bx6gxTOoja9k',
    projectId: 'qQzsBf58SqzNJX45iggq96',
  },
  RateAndCommentCount: {
    Component: dynamic(() => import('.plasmic/plasmic/ravi_r_r/PlasmicRateAndCommentCount').then(m => withAllowedProps(m.default, m.PlasmicRateAndCommentCount__ArgProps))),
    id: 'u3Jgb_UfiULc',
    projectId: 'qQzsBf58SqzNJX45iggq96',
  },
  RateProgressBar: {
    Component: dynamic(() => import('.plasmic/plasmic/ravi_r_r/PlasmicRateProgressBar').then(m => withAllowedProps(m.default, m.PlasmicRateProgressBar__ArgProps))),
    id: 'YorKPsj5-KCA',
    projectId: 'qQzsBf58SqzNJX45iggq96',
  },
  // اسکلتِ خودمان — مخفی‌سازی «پیگیری تأخیر / پشتیبانی این نوبت» بدون دست‌کاری فایل auto-generated
  ReceiptActionButtons: {
    Component: dynamic(() =>
      Promise.all([import('.plasmic/ReceiptActionButtons'), import('.plasmic/plasmic/paziresh_24/PlasmicReceiptActionButtons')]).then(([c, a]) => withAllowedProps(c.default, a.PlasmicReceiptActionButtons__ArgProps, ['hideCancel'])),
    ),
    id: 'EifS7TB9I3zC',
    projectId: 'iDYgiKJB9Yi7CUB81stQBK',
  },
  Services: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24/PlasmicServices').then(m => withAllowedProps(m.default, m.PlasmicServices__ArgProps))),
    id: 'q3FGMz6XNu9L',
    projectId: 'iDYgiKJB9Yi7CUB81stQBK',
  },
  Activity: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24/PlasmicActivity').then(m => withAllowedProps(m.default, m.PlasmicActivity__ArgProps))),
    id: 'pggD1apWa_wW',
    projectId: 'iDYgiKJB9Yi7CUB81stQBK',
  },
  About: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24/PlasmicAbout').then(m => withAllowedProps(m.default, m.PlasmicAbout__ArgProps))),
    id: '6mjf324FkOZF',
    projectId: 'iDYgiKJB9Yi7CUB81stQBK',
  },
  LocationSelectionScript: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24_search/PlasmicLocationSelectionScript').then(m => withAllowedProps(m.default, m.PlasmicLocationSelectionScript__ArgProps))),
    id: '5bzKtjF_q24p',
    projectId: 'sMdpLWyxbzDCruwMRffW2m',
  },
  // اسکلتِ خودمان روی همان کارتِ Plasmic — «حضورِ پزشک» را اضافه می‌کند و بقیه‌ی propها را عیناً پاس می‌دهد.
  // این مسیرِ رندرِ دومِ کارت است (پروفایلِ مرکز)؛ مسیرِ لیستینگ از قبل از همین اسکلت می‌آید.
  ProductCard: {
    Component: dynamic(() =>
      Promise.all([import('.plasmic/ProductCard'), import('.plasmic/plasmic/paziresh_24_search/PlasmicProductCard')]).then(([c, a]) => withAllowedProps(c.default, a.PlasmicProductCard__ArgProps)),
    ),
    id: 'NhMGML-3Q4Pu',
    projectId: 'sMdpLWyxbzDCruwMRffW2m',
  },
  SearchResults: {
    // Preloadable: the search page preloads it so its first list renders with the data, not a frame later (preloadable.tsx).
    ...preloadable(
      dynamic(() => import('.plasmic/plasmic/paziresh_24_search/PlasmicSearchResults').then(m => withAllowedProps(m.default, m.PlasmicSearchResults__ArgProps, ['overrides']))),
      () => import('.plasmic/plasmic/paziresh_24_search/PlasmicSearchResults').then(m => withAllowedProps(m.default, m.PlasmicSearchResults__ArgProps, ['overrides'])),
    ),
    id: 'XhSI4pxMLR3L',
    projectId: 'sMdpLWyxbzDCruwMRffW2m',
  },
  Schedules: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24_booking/PlasmicBookingSchedules').then(m => withAllowedProps(m.default, m.PlasmicBookingSchedules__ArgProps))),
    id: 'Mt_WMP6AHSGv',
    projectId: '8NbkXymcLwvMUC2yXeRrWk',
  },
  SearchRequest: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24_search/PlasmicSearchRequest').then(m => withAllowedProps(m.default, m.PlasmicSearchRequest__ArgProps))),
    id: '35vwUOYdUX87',
    projectId: 'sMdpLWyxbzDCruwMRffW2m',
  },
  AddressesWrapper: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24_booking/PlasmicBookingAddressesWrapper').then(m => withAllowedProps(m.default, m.PlasmicBookingAddressesWrapper__ArgProps))),
    id: 'Z7E4nvI5-Dtv',
    projectId: '8NbkXymcLwvMUC2yXeRrWk',
  },
  AddressesCard: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24_booking/PlasmicBookingAddressesCard').then(m => withAllowedProps(m.default, m.PlasmicBookingAddressesCard__ArgProps))),
    id: 'z1k0-vbkFtby',
    projectId: '8NbkXymcLwvMUC2yXeRrWk',
  },
  BookingServiceList: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24_booking/PlasmicBookingServiceList').then(m => withAllowedProps(m.default, m.PlasmicBookingServiceList__ArgProps))),
    id: 'fpdRSzutXHoq',
    projectId: '8NbkXymcLwvMUC2yXeRrWk',
  },
  ProfileHead: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24_profile/PlasmicProfileHead').then(m => withAllowedProps(m.default, m.PlasmicProfileHead__ArgProps))),
    id: 'fKBGdItR62E2',
    projectId: '7r312uiqyadpVPdnRoAggk',
  },
  ProfileAbout: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24_profile/PlasmicProfileAbout').then(m => withAllowedProps(m.default, m.PlasmicProfileAbout__ArgProps))),
    id: 'VlJvd0AHTT9_',
    projectId: '7r312uiqyadpVPdnRoAggk',
  },
  ProfileActivity: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24_profile/PlasmicProfileActivity').then(m => withAllowedProps(m.default, m.PlasmicProfileActivity__ArgProps))),
    id: 'LIHtK_X7GpDY',
    projectId: '7r312uiqyadpVPdnRoAggk',
  },
  ProfileSeo: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24_profile/PlasmicProfileSeo').then(m => withAllowedProps(m.default, m.PlasmicProfileSeo__ArgProps))),
    id: 'AyZkzO0Ld0SI',
    projectId: '7r312uiqyadpVPdnRoAggk',
  },
  ProfileGallery: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24_profile/PlasmicProfileGallery').then(m => withAllowedProps(m.default, m.PlasmicProfileGallery__ArgProps))),
    id: '-M0f8W0T-8eT',
    projectId: '7r312uiqyadpVPdnRoAggk',
  },
  FilterListView: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24_search/PlasmicFilterListView').then(m => withAllowedProps(m.default, m.PlasmicFilterListView__ArgProps))),
    id: 'Z5K_XiJUvXFD',
    projectId: 'sMdpLWyxbzDCruwMRffW2m',
  },
  FilterExpertiseView: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24_search/PlasmicFilterExpertiseView').then(m => withAllowedProps(m.default, m.PlasmicFilterExpertiseView__ArgProps))),
    id: 'N8MUho5TZvBe',
    projectId: 'sMdpLWyxbzDCruwMRffW2m',
  },
  FilterSelectedView: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24_search/PlasmicFilterSelectedView').then(m => withAllowedProps(m.default, m.PlasmicFilterSelectedView__ArgProps))),
    id: 'hzanYC1FO-Wr',
    projectId: 'sMdpLWyxbzDCruwMRffW2m',
  },
  FilterRow: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24_search/PlasmicFilterRow').then(m => withAllowedProps(m.default, m.PlasmicFilterRow__ArgProps))),
    id: 'OC23iWRW1Dia',
    projectId: 'sMdpLWyxbzDCruwMRffW2m',
  },
  HomePageShortcuts: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24_search/PlasmicHomePageShortcuts').then(m => withAllowedProps(m.default, m.PlasmicHomePageShortcuts__ArgProps))),
    id: 'pfHDjmK7Jo_j',
    projectId: 'sMdpLWyxbzDCruwMRffW2m',
  },
  OnlineVisit: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24_search/PlasmicOnlineVisit').then(m => withAllowedProps(m.default, m.PlasmicOnlineVisit__ArgProps))),
    id: 'kiuZ6fZVjKcZ',
    projectId: 'sMdpLWyxbzDCruwMRffW2m',
  },
  SearchInput: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24_search/PlasmicSearch').then(m => withAllowedProps(m.default, m.PlasmicSearch__ArgProps))),
    id: 'd_qMEJ14UZf0',
    projectId: 'sMdpLWyxbzDCruwMRffW2m',
  },
  RecentSearch: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24_search/PlasmicRecentSearch').then(m => withAllowedProps(m.default, m.PlasmicRecentSearch__ArgProps))),
    id: 'ARWFU9130hug',
    projectId: 'sMdpLWyxbzDCruwMRffW2m',
  },
  SeoBox: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24_search/PlasmicSeoBox').then(m => withAllowedProps(m.default, m.PlasmicSeoBox__ArgProps))),
    id: 'LCOgDdMa8kxO',
    projectId: 'sMdpLWyxbzDCruwMRffW2m',
  },
  Sort: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24_search/PlasmicSort').then(m => withAllowedProps(m.default, m.PlasmicSort__ArgProps))),
    id: 's-wlX7BnSeTl',
    projectId: 'sMdpLWyxbzDCruwMRffW2m',
  },
  ConsultBanner: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24_search/PlasmicConsultBanner').then(m => withAllowedProps(m.default, m.PlasmicConsultBanner__ArgProps))),
    id: 'KmDr0VPQLI2_',
    projectId: 'sMdpLWyxbzDCruwMRffW2m',
  },
  ResultView: {
    Component: dynamic(() => import('.plasmic/plasmic/paziresh_24_search/PlasmicResultView').then(m => withAllowedProps(m.default, m.PlasmicResultView__ArgProps))),
    id: 'NYqLbXm7Qk5C',
    projectId: 'sMdpLWyxbzDCruwMRffW2m',
  },
  Risman: {
    Component: dynamic(() => import('.plasmic/plasmic/risman/PlasmicRisman').then(m => withAllowedProps(m.default, m.PlasmicRisman__ArgProps))),
    id: 'KinPlL1Jj1j3',
    projectId: 'bN5uNsAhTefwW3S14VjvMG',
  },
};

